export type QueryValue = string | number | boolean | null | undefined;

/**
 * Build a URL query-string from an object. Skips falsy entries
 * (`undefined`, `null`, `''`, `0`, `false`) — matches the project's legacy
 * `if (params.x) { ... }` truthy-check pattern at the previous call sites.
 *
 * If you need to send a literal `0` or `false` as a filter value, pass it as
 * a string (`'0'` / `'false'`) — non-empty strings are preserved verbatim.
 *
 * Returns `'?k=v&k=v'`, or `''` when every entry is filtered out — safe to
 * append unconditionally.
 *
 * Values are not URL-encoded — matches the project's existing convention.
 * Encode the value at the call site if it can contain reserved characters.
 */
export function buildQuery(params?: Record<string, QueryValue>): string {
  if (!params) return '';
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (!value) continue;
    parts.push(`${key}=${value}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

/** The `date_after` / `date_before` filter of a start / end date range. */
export function dateRangeFilter(range?: { startDate?: string; endDate?: string }) {
  return { date_after: range?.startDate, date_before: range?.endDate };
}

export interface FarmDateParams {
  farm: number;
  startDate?: string;
  endDate?: string;
}

/** The query of a farm's rows over a date range, as the harvester endpoints take it. */
export function farmDateQuery(params: FarmDateParams) {
  return buildQuery({ farm: params.farm, ...dateRangeFilter(params) });
}
